// src/App.js
// Root application component.
// Providers are nested in order: Auth → Currency → Cart → Router

import React, { Suspense, useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { WishlistProvider } from "./context/WishlistContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import CookieBanner from "./components/CookieBanner";
import BackToTop from "./components/BackToTop";
import EmailPopup from "./components/EmailPopup";
import CountdownGate from "./components/CountdownGate";
import Home from "./pages/Home";
import OurScents from "./pages/OurScents";
import ProductDetail from "./pages/ProductDetail";
import TheTeam from "./pages/TheTeam";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Account from "./pages/Account";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import ShippingPolicy from "./pages/ShippingPolicy";
import LegalNotice from "./pages/LegalNotice";
import FAQ from "./pages/FAQ";
import Wishlist from "./pages/Wishlist";
import Locations from "./pages/Locations";
import Quiz from "./pages/Quiz";
import Spin from "./pages/Spin";
import NotFound from "./pages/NotFound";
import AnnouncementBanner from "./components/AnnouncementBanner";
import { trackPageView, trackLivePageView } from "./lib/analytics";

// Private analytics dashboard. Loaded on demand so shoppers never download it.
const AdminAnalytics = React.lazy(() => import("./pages/AdminAnalytics"));

// Scrolls to top of page on every route change
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

// Tells Meta about client-side route changes (GA4 tracks these itself).
// Skips the first render: the pixel records the landing page when it loads.
function RouteTracker() {
    const { pathname } = useLocation();
    const firstRender = React.useRef(true);
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        trackPageView();
    }, [pathname]);
    // Our own analytics wants every page, including the first one.
    useEffect(() => {
        trackLivePageView(pathname);
    }, [pathname]);
    return null;
}

// Routes not covered by these must not exist in the sitemap or robots' allow list
const HIDDEN_PROMO_ROUTES = ['/spin'];

// Private pages that also render without the shop's header, footer and popups.
const ADMIN_ROUTES = ['/admin/analytics'];

// Suppresses the normal site chrome (nav, footer, cart, cookie banner, email
// popup) on hidden promo pages so they read as standalone experiences rather
// than as part of the main site.
function SiteChrome({ theme, toggleTheme, children }) {
    const { pathname } = useLocation();
    const isHiddenPromo = HIDDEN_PROMO_ROUTES.includes(pathname);
    const isAdmin = ADMIN_ROUTES.includes(pathname);

    if (isHiddenPromo || isAdmin) return children;

    return (
        <>
            <AnnouncementBanner />
            <Header theme={theme} toggleTheme={toggleTheme} />
            {children}
            <Footer />
            <CartDrawer />
            <CookieBanner />
            <BackToTop />
            <EmailPopup />
        </>
    );
}

function App() {
    const [theme, setTheme] = useState("light");

    // Load saved theme from localStorage on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem("theme") || "light";
        setTheme(savedTheme);
        if (savedTheme === "dark") {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
    }, []);

    // Toggle between light and dark mode and persist preference
    const toggleTheme = () => {
        const newTheme = theme === "light" ? "dark" : "light";
        setTheme(newTheme);
        localStorage.setItem("theme", newTheme);
        if (newTheme === "dark") {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
    };

    return (
        <ErrorBoundary>
        <CountdownGate>
            {/* AuthProvider — manages customer login state */}
            <AuthProvider>
                {/* CurrencyProvider — auto-detects country and manages currency selection */}
                <CurrencyProvider>
                    {/* WishlistProvider — manages saved products per customer */}
                    <WishlistProvider>
                        {/* CartProvider — manages Shopify cart state */}
                        <CartProvider>
                            <BrowserRouter>
                                <ScrollToTop />
                                <RouteTracker />
                                <div className="App min-h-screen bg-background text-foreground" style={{ maxWidth: '100vw', overflowX: 'hidden' }}>
                                    <SiteChrome theme={theme} toggleTheme={toggleTheme}>
                                        <Routes>
                                            <Route path="/" element={<Home />} />
                                            <Route path="/scents" element={<OurScents />} />
                                            <Route path="/product/:handle" element={<ProductDetail />} />
                                            <Route path="/team" element={<TheTeam />} />
                                            <Route path="/contact" element={<Contact />} />
                                            <Route path="/login" element={<Login />} />
                                            <Route path="/register" element={<Register />} />
                                            <Route path="/account" element={<Account />} />
                                            <Route path="/terms" element={<TermsOfService />} />
                                            <Route path="/privacy" element={<PrivacyPolicy />} />
                                            <Route path="/refunds" element={<RefundPolicy />} />
                                            <Route path="/shipping" element={<ShippingPolicy />} />
                                            <Route path="/legal" element={<LegalNotice />} />
                                            <Route path="/faq" element={<FAQ />} />
                                            <Route path="/wishlist" element={<Wishlist />} />
                                            <Route path="/locations" element={<Locations />} />
                                            <Route path="/quiz" element={<Quiz />} />
                                            {/* Hidden QR-only promo page — deliberately not linked anywhere in the site */}
                                            <Route path="/spin" element={<Spin />} />
                                            {/* Private analytics dashboard — sign-in required, not linked anywhere */}
                                            <Route
                                                path="/admin/analytics"
                                                element={
                                                    <Suspense fallback={null}>
                                                        <AdminAnalytics />
                                                    </Suspense>
                                                }
                                            />
                                            {/* Catch-all route — renders 404 page */}
                                            <Route path="*" element={<NotFound />} />
                                        </Routes>
                                    </SiteChrome>
                                    <Toaster />
                                </div>
                            </BrowserRouter>
                        </CartProvider>
                    </WishlistProvider>
                </CurrencyProvider>
            </AuthProvider>
        </CountdownGate>
        </ErrorBoundary>
    );
}

export default App;
